import { useEffect, useRef } from 'react';
import { cn } from '../lib/utils';

// Type definitions for widget props
export interface UserIntuitionWidgetProps {
  // Required props
  publicKey: string;
  assistantId: string;
  
  // Optional props
  inviteId?: string;
  mode?: 'voice' | 'chat' | 'hybrid';
  theme?: 'light' | 'dark';
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left' | 'center';
  size?: 'full' | 'compact' | 'minimal';
  borderRadius?: 'none' | 'small' | 'medium' | 'large';
  
  // Colors & Styling
  baseBgColor?: string;
  accentColor?: string;
  ctaButtonColor?: string;
  ctaButtonTextColor?: string;
  baseColor?: string;
  buttonBaseColor?: string;
  buttonAccentColor?: string;
  
  // Text Content
  title?: string;
  subtitle?: string;
  ctaTitle?: string;
  ctaSubtitle?: string;
  startButtonText?: string;
  endButtonText?: string;
  mainLabel?: string;
  
  // Voice Mode Messages
  voiceEmptyMessage?: string;
  voiceActiveEmptyMessage?: string;
  emptyVoiceMessage?: string;
  emptyVoiceActiveMessage?: string;
  
  // Chat Mode Messages
  chatEmptyMessage?: string;
  chatFirstMessage?: string;
  chatPlaceholder?: string;
  emptyChatMessage?: string;
  
  // Hybrid Mode Messages
  hybridEmptyMessage?: string;
  emptyHybridMessage?: string;
  
  // Voice Configuration
  voiceShowTranscript?: boolean;
  showConversationArea?: boolean;
  voiceAutoReconnect?: boolean;
  reconnectStorageKey?: string;
  showTranscript?: boolean;
  
  // Consent & Terms
  consentRequired?: boolean;
  consentTitle?: string;
  consentContent?: string;
  consentStorageKey?: string;
  requireConsent?: boolean;
  termsContent?: string;
  localStorageKey?: string;
  
  // Advanced Configuration
  assistantOverrides?: Record<string, unknown>;
  assistant?: Record<string, unknown>;
  apiUrl?: string;
  showWidget?: boolean;
  showBasedOnUrl?: string[];
  delayInterval?: number;
  
  // Component styling
  className?: string;
}

/**
 * UserIntuitionWidget - A React component wrapper for the User Intuition AI widget
 * 
 * This component initializes the User Intuition widget using the WidgetLoader API
 * from the @userintuition-ai/web package.
 * 
 * The widget includes a close button that appears when the widget is collapsed (not expanded).
 * Clicking the close button will hide the widget for 3 months using localStorage persistence.
 * 
 * @example
 * ```tsx
 * <UserIntuitionWidget
 *   publicKey="your-public-key"
 *   assistantId="your-assistant-id"
 *   mode="voice"
 *   theme="dark"
 *   position="bottom-right"
 * />
 * ```
 * 
 * @example With custom colors for close button styling
 * ```tsx
 * <UserIntuitionWidget
 *   publicKey="your-public-key"
 *   assistantId="your-assistant-id"
 *   ctaButtonColor="#000000"
 *   ctaButtonTextColor="#ffffff"
 *   theme="dark"
 * />
 * ```
 */
export function UserIntuitionWidget({
  publicKey,
  assistantId,
  inviteId,
  mode = 'voice',
  theme = 'dark',
  position = 'bottom-right',
  size = 'full',
  borderRadius = 'large',
  baseBgColor = '#000000',
  accentColor = '#14B8A6',
  ctaButtonColor = '#000000',
  ctaButtonTextColor = '#ffffff',
  title = 'Shape our roadmap in 10 min',
  subtitle,
  startButtonText = 'Start',
  endButtonText = 'End Call',
  voiceShowTranscript = true,
  showConversationArea = true,
  showWidget = true,
  consentRequired = true,
  consentTitle = 'Terms and conditions',
  consentContent = 'By clicking "Agree," and each time I interact with this AI agent, I consent to the recording, storage, and sharing of my communications with third-party service providers, and as otherwise described in our Terms of Service.',
  consentStorageKey = 'userintuition_widget_consent',
  showBasedOnUrl,
  delayInterval = 0,
  className,
}: UserIntuitionWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetInitializedRef = useRef(false);

  useEffect(() => {
    // Only initialize once
    if (widgetInitializedRef.current || !containerRef.current) {
      return;
    }

    // Dynamic import of the widget loader
    const initializeWidget = async () => {
      try {
        // Try to load WidgetLoader from window object (after script loads) or from module
        // The package is a UMD bundle, so it may be available on window
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const windowAny = window as any;
        let WidgetLoader = windowAny.WidgetLoader;
        
        if (!WidgetLoader) {
          // Try to import from the module
          try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const widgetModule = await import('../../../dist/embed/widget.umd.min.js' as any);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            WidgetLoader = (widgetModule as any).WidgetLoader || (widgetModule as any).default?.WidgetLoader || (widgetModule as any).default;
          } catch (importError) {
            console.warn('Could not import widget module, trying to load script instead', importError);
            // Fall back to web component approach
            return;
          }
        }

        if (!WidgetLoader) {
          console.error('WidgetLoader not found. Please ensure the widget script is loaded.');
          return;
        }

        // Prepare props for the widget
        // Note: The close button is automatically included when the widget is collapsed.
        // It uses ctaButtonTextColor for styling and hides the widget for 3 months when clicked.
        const widgetProps: Record<string, unknown> = {
          publicKey,
          assistantId,
          mode,
          theme,
          position,
          size,
          borderRadius,
          baseBgColor,
          accentColor,
          ctaButtonColor,
          ctaButtonTextColor, // Used for close button text color
          title,
          subtitle,
          startButtonText,
          endButtonText,
          voiceShowTranscript,
          showConversationArea,
          showWidget,
          consentRequired,
          consentTitle,
          consentContent,
          consentStorageKey,
          delayInterval,
        };

        // Add optional props if provided
        if (inviteId) {
          widgetProps.inviteId = inviteId;
        }
        if (showBasedOnUrl) {
          widgetProps.showBasedOnUrl = showBasedOnUrl;
        }

        // Check if WidgetLoader is a class constructor
        const isClass = typeof WidgetLoader === 'function' && 
          WidgetLoader.prototype && 
          WidgetLoader.prototype.constructor === WidgetLoader;

        // Initialize the widget
        if (isClass) {
          // If it's a class, instantiate it with new
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          new WidgetLoader({
            container: containerRef.current,
            component: 'VapiWidget',
            props: widgetProps,
          });
        } else {
          // Otherwise, call it as a function
          WidgetLoader({
            container: containerRef.current,
            component: 'VapiWidget',
            props: widgetProps,
          });
        }

        widgetInitializedRef.current = true;
      } catch (error) {
        console.error('Error initializing User Intuition widget:', error);
        // Fall back to web component approach if WidgetLoader fails
      }
    };

    // Add delay if specified
    if (delayInterval > 0) {
      const timeoutId = setTimeout(() => {
        initializeWidget();
      }, delayInterval);
      
      return () => {
        clearTimeout(timeoutId);
      };
    } else {
      initializeWidget();
    }

    // Cleanup function
    return () => {
      // Clean up widget if needed
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
      widgetInitializedRef.current = false;
    };
  }, [
    publicKey,
    assistantId,
    inviteId,
    mode,
    theme,
    position,
    size,
    borderRadius,
    baseBgColor,
    accentColor,
    ctaButtonColor,
    ctaButtonTextColor,
    title,
    subtitle,
    startButtonText,
    endButtonText,
    voiceShowTranscript,
    showConversationArea,
    showWidget,
    consentRequired,
    consentTitle,
    consentContent,
    consentStorageKey,
    delayInterval,
    showBasedOnUrl,
  ]);

  return (
    <div
      ref={containerRef}
      className={cn('userintuition-widget-container', className)}
      data-testid="userintuition-widget"
    />
  );
}

/**
 * UserIntuitionWidgetWebComponent - Recommended implementation using custom web component
 * 
 * This is the recommended approach as it matches how the package is designed to be used.
 * The package is primarily a UMD bundle that registers a custom web component.
 * 
 * The widget includes a close button that appears when the widget is collapsed (not expanded).
 * Clicking the close button will hide the widget for 3 months using localStorage persistence.
 * 
 * @example
 * ```tsx
 * <UserIntuitionWidgetWebComponent
 *   publicKey="your-public-key"
 *   assistantId="your-assistant-id"
 *   mode="voice"
 *   theme="dark"
 *   position="bottom-right"
 * />
 * ```
 * 
 * @example With custom colors for close button styling
 * ```tsx
 * <UserIntuitionWidgetWebComponent
 *   publicKey="your-public-key"
 *   assistantId="your-assistant-id"
 *   ctaButtonColor="#000000"
 *   ctaButtonTextColor="#ffffff"
 *   theme="dark"
 * />
 * ```
 */
export function UserIntuitionWidgetWebComponent({
  publicKey,
  assistantId,
  inviteId,
  mode = 'voice',
  theme = 'dark',
  position = 'bottom-right',
  size = 'full',
  borderRadius = 'large',
  baseBgColor = '#000000',
  accentColor = '#14B8A6',
  ctaButtonColor = '#000000',
  ctaButtonTextColor = '#ffffff',
  title = 'Shape our roadmap in 10 min',
  subtitle,
  startButtonText = 'Start',
  endButtonText = 'End Call',
  voiceShowTranscript = true,
  showConversationArea = true,
  showWidget = true,
  consentRequired = true,
  consentTitle = 'Terms and conditions',
  consentContent = 'By clicking "Agree," and each time I interact with this AI agent, I consent to the recording, storage, and sharing of my communications with third-party service providers, and as otherwise described in our Terms of Service.',
  consentStorageKey = 'userintuition_widget_consent',
  showBasedOnUrl,
  delayInterval = 0,
  className,
}: UserIntuitionWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // Load the widget script if not already loaded
    const scriptId = 'userintuition-widget-script';
    let script = document.getElementById(scriptId) as HTMLScriptElement;

    const createWidget = () => {
      if (!containerRef.current) return;

      // Wait for custom element to be defined
      if (customElements.get('userintuition-widget')) {
        const widgetElement = document.createElement('userintuition-widget');
        
        // Set required attributes
        widgetElement.setAttribute('public-key', publicKey);
        widgetElement.setAttribute('assistant-id', assistantId);
        
        // Set optional attributes
        if (inviteId) {
          widgetElement.setAttribute('invite-id', inviteId);
        }
        widgetElement.setAttribute('mode', mode);
        widgetElement.setAttribute('theme', theme);
        widgetElement.setAttribute('position', position);
        widgetElement.setAttribute('size', size);
        widgetElement.setAttribute('border-radius', borderRadius);
        widgetElement.setAttribute('base-bg-color', baseBgColor);
        widgetElement.setAttribute('accent-color', accentColor);
        widgetElement.setAttribute('cta-button-color', ctaButtonColor);
        // cta-button-text-color is used for close button text color
        widgetElement.setAttribute('cta-button-text-color', ctaButtonTextColor);
        widgetElement.setAttribute('title', title);
        if (subtitle) {
          widgetElement.setAttribute('subtitle', subtitle);
        }
        widgetElement.setAttribute('start-button-text', startButtonText);
        widgetElement.setAttribute('end-button-text', endButtonText);
        widgetElement.setAttribute('voice-show-transcript', String(voiceShowTranscript));
        widgetElement.setAttribute('show-conversation-area', String(showConversationArea));
        widgetElement.setAttribute('show-widget', String(showWidget));
        widgetElement.setAttribute('consent-required', String(consentRequired));
        widgetElement.setAttribute('consent-title', consentTitle);
        widgetElement.setAttribute('consent-content', consentContent);
        widgetElement.setAttribute('consent-storage-key', consentStorageKey);
        
        if (delayInterval > 0) {
          widgetElement.setAttribute('delay-interval', String(delayInterval));
        }
        
        if (showBasedOnUrl && showBasedOnUrl.length > 0) {
          widgetElement.setAttribute('show-based-on-url', JSON.stringify(showBasedOnUrl));
        }

        // Clear container and append widget
        containerRef.current.innerHTML = '';
        containerRef.current.appendChild(widgetElement);
      } else {
        // Retry after a short delay if custom element not yet defined
        setTimeout(createWidget, 100);
      }
    };

    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://unpkg.com/@userintuition-ai/web/dist/embed/widget.umd.min.js';
      script.async = true;
      script.type = 'text/javascript';
      
      // Wait for script to load before creating widget
      script.onload = () => {
        // Wait a bit for custom element registration
        setTimeout(createWidget, 100);
      };
      
      script.onerror = () => {
        console.error('Failed to load User Intuition widget script');
      };
      
      document.body.appendChild(script);
    } else {
      // Script already loaded, create widget immediately or after short delay
      createWidget();
    }

    return () => {
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };
  }, [
    publicKey,
    assistantId,
    inviteId,
    mode,
    theme,
    position,
    size,
    borderRadius,
    baseBgColor,
    accentColor,
    ctaButtonColor,
    ctaButtonTextColor,
    title,
    subtitle,
    startButtonText,
    endButtonText,
    voiceShowTranscript,
    showConversationArea,
    showWidget,
    consentRequired,
    consentTitle,
    consentContent,
    consentStorageKey,
    delayInterval,
    showBasedOnUrl,
  ]);

  return (
    <div
      ref={containerRef}
      className={cn('userintuition-widget-container', className)}
      data-testid="userintuition-widget"
    />
  );
}

// Export default as the web component version (recommended - more reliable)
// This is the recommended implementation as it matches the package's UMD bundle structure
export default UserIntuitionWidgetWebComponent;

